/** Static catalogs that are the same for every client. Per-client choices live in PortalConfig. */

/** Brand kit rows kept for imports and older data; the Assets step now sorts uploads by category instead. */
export const BRAND_ASSET_KEYS = [
  { key: 'primary_logo', label: 'Primary logo' },
  { key: 'secondary_logo', label: 'Secondary logo' },
  { key: 'icon', label: 'Icon or monogram' },
  { key: 'fonts', label: 'Fonts' },
  { key: 'colors', label: 'Brand colors' },
  { key: 'style_guide', label: 'Style guide' },
  { key: 'animations', label: 'Animations' },
  { key: 'templates', label: 'Templates' },
] as const
export type BrandAssetKey = (typeof BRAND_ASSET_KEYS)[number]['key']

export const LINK_KEYS = [
  { key: 'food', label: 'Food menu' },
  { key: 'drinks', label: 'Drink menu' },
  { key: 'reservations', label: 'Reservations' },
  { key: 'takeout', label: 'Takeout / online ordering' },
  { key: 'events', label: 'Private events' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'presentation', label: 'Food & drink presentation sheets' },
] as const
export type LinkKey = (typeof LINK_KEYS)[number]['key']

export const FAQ_GROUPS = ['contact', 'service', 'dietary', 'general'] as const
export type FaqGroup = (typeof FAQ_GROUPS)[number]
export const FAQ_GROUP_LABELS: Record<FaqGroup, string> = {
  contact: 'Contact',
  service: 'Service',
  dietary: 'Dietary',
  general: 'General',
}

export type FaqQuestion = { id: string; group: FaqGroup; order: number; label: string; active: boolean }

/** Seed for the faqQuestions collection. Ids are stable; staff can add or deactivate in settings. */
export const FAQ_CATALOG: FaqQuestion[] = [
  { id: 'phone', group: 'contact', order: 10, label: 'Main phone line (the number on Google and your website)', active: true },
  { id: 'parties', group: 'contact', order: 20, label: 'Private events contact', active: true },
  { id: 'hiring', group: 'contact', order: 30, label: 'Hiring and applications', active: true },
  { id: 'collabs', group: 'contact', order: 40, label: 'Collaboration and marketing inquiries', active: true },
  { id: 'pr', group: 'contact', order: 50, label: 'PR and press inquiries', active: true },
  { id: 'music', group: 'contact', order: 60, label: 'Live music and DJ inquiries', active: true },
  { id: 'complaints', group: 'contact', order: 70, label: 'Service complaints and management contact', active: true },
  { id: 'vendors', group: 'contact', order: 80, label: 'Distributors and liquor rep inquiries', active: true },
  { id: 'lost', group: 'contact', order: 90, label: 'Lost items, theft, incidents', active: true },
  { id: 'birthdays', group: 'service', order: 100, label: 'Do you do anything for birthdays or anniversaries?', active: true },
  { id: 'maxtime', group: 'service', order: 110, label: 'Is there a maximum dining time?', active: true },
  { id: 'dress', group: 'service', order: 120, label: 'Is there a dress code? Is it strictly enforced?', active: true },
  { id: 'reservations', group: 'service', order: 130, label: 'Is a reservation required? How do guests book?', active: true },
  { id: 'cake', group: 'service', order: 140, label: 'Can guests bring their own cake, balloons, decorations? Any cakeage fee?', active: true },
  { id: 'wine', group: 'service', order: 150, label: 'Can guests bring their own wine? Bottle limit or corkage fee?', active: true },
  { id: 'partysize', group: 'service', order: 160, label: 'Minimum or maximum party size? When should groups call?', active: true },
  { id: 'giftcards', group: 'service', order: 170, label: 'Gift cards', active: true },
  { id: 'halal', group: 'dietary', order: 200, label: 'Is the food halal, or are there halal options?', active: true },
  { id: 'glutenfree', group: 'dietary', order: 210, label: 'Gluten-free options? (e.g. gluten-free pasta, bread)', active: true },
  { id: 'fryer', group: 'dietary', order: 220, label: 'Separate fryer for gluten allergies?', active: true },
  { id: 'vegan', group: 'dietary', order: 230, label: 'Vegan or vegetarian options?', active: true },
  { id: 'nuts', group: 'dietary', order: 240, label: 'Accommodations for nut allergies?', active: true },
  { id: 'shellfish', group: 'dietary', order: 250, label: 'Accommodations for shellfish allergies?', active: true },
  { id: 'zeroproof', group: 'dietary', order: 260, label: 'Zero-proof or N/A cocktails?', active: true },
  { id: 'parking', group: 'general', order: 300, label: 'Parking? Validation or valet?', active: true },
  { id: 'kids', group: 'general', order: 310, label: 'Are children welcome?', active: true },
  { id: 'kidshours', group: 'general', order: 315, label: 'Are there times children aren’t allowed? (e.g. no children at dinner)', active: true },
  { id: 'outdoor', group: 'general', order: 320, label: 'Is there outdoor seating?', active: true },
  { id: 'pets', group: 'general', order: 325, label: 'Are dogs or other pets allowed?', active: true },
]

/**
 * How uploaded files are grouped on the Assets step. A file's category is guessed from its type and name
 * (see guessUploadCategory) until someone picks one.
 */
export const UPLOAD_CATEGORIES = [
  { key: 'logo', label: 'Logos & marks' },
  { key: 'font', label: 'Fonts' },
  { key: 'guide', label: 'Guides & documents' },
  { key: 'video', label: 'Video & animation' },
  { key: 'photo', label: 'Photos & graphics' },
  { key: 'other', label: 'Other' },
] as const
export type UploadCategory = (typeof UPLOAD_CATEGORIES)[number]['key']

const ext = (name: string) => /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? ''

/** Best guess at what an uploaded file is, from its extension, type and name. */
export function guessUploadCategory(name: string, contentType: string): UploadCategory {
  const e = ext(name)
  const type = contentType.toLowerCase()
  if (type.startsWith('font/') || ['otf', 'ttf', 'woff', 'woff2'].includes(e)) return 'font'
  if (['ai', 'eps', 'svg'].includes(e) || type === 'application/postscript' || type === 'image/svg+xml') return 'logo'
  if (type.startsWith('image/') && /logo|mark|monogram|icon|lockup|emblem/i.test(name)) return 'logo'
  if (type === 'application/pdf' || e === 'pdf') return 'guide'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('image/')) return 'photo'
  return 'other'
}

/** Some browsers send no type for fonts and Illustrator files; storage needs one to accept the upload. */
export function uploadContentType(name: string, browserType: string): string {
  if (browserType && browserType !== 'application/octet-stream') return browserType
  const byExt: Record<string, string> = { otf: 'font/otf', ttf: 'font/ttf', woff: 'font/woff', woff2: 'font/woff2', ai: 'application/postscript', eps: 'application/postscript', svg: 'image/svg+xml', pdf: 'application/pdf', zip: 'application/zip' }
  return byExt[ext(name)] ?? browserType
}

/**
 * What a contact handles. Ids match the FAQ questions they answer, so "Private events contact" lists every
 * contact tagged `parties` and nobody retypes a phone number into an answer.
 */
export const CONTACT_TOPICS = [
  { id: 'phone', label: 'Main line' },
  { id: 'reservations', label: 'Reservations' },
  { id: 'parties', label: 'Private events' },
  { id: 'complaints', label: 'Complaints & management' },
  { id: 'pr', label: 'PR & press' },
  { id: 'collabs', label: 'Collaborations & marketing' },
  { id: 'hiring', label: 'Hiring' },
  { id: 'music', label: 'Live music & DJs' },
  { id: 'vendors', label: 'Distributors & liquor reps' },
  { id: 'lost', label: 'Lost items & incidents' },
] as const
export type ContactTopic = (typeof CONTACT_TOPICS)[number]['id']
export const CONTACT_TOPIC_IDS: readonly string[] = CONTACT_TOPICS.map((t) => t.id)

/**
 * Turns an older free-text "Route to" note and its email into contacts. A note can hold several addresses
 * ("info@a.com · info@b.com"), so each email becomes its own contact; "Name, Title" splits into name and title.
 */
export function splitLegacyContact(name: string, email: string): { name: string; role: string; email: string }[] {
  const parts = [name, email].flatMap((s) => s.split(/\s*[·;|]\s*/)).map((s) => s.trim()).filter(Boolean)
  const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
  const emails = [...new Set(parts.filter(isEmail))]
  const people = parts.filter((p) => !isEmail(p)).map((p) => {
    const [n, ...rest] = p.split(/\s*,\s*/)
    return { name: n ?? '', role: rest.join(', ') }
  })
  if (people.length <= 1 && emails.length <= 1) return people.length || emails.length ? [{ name: people[0]?.name ?? '', role: people[0]?.role ?? '', email: emails[0] ?? '' }] : []
  return [...emails.map((e) => ({ name: '', role: '', email: e })), ...people.map((p) => ({ ...p, email: '' }))]
}
